import { VERTICAL_ORIENTATION } from './constants/editorConstants';

export const BUS_THICKNESS_PX = 20;
export const MIN_BUS_HANDLES = 3;
export const BUS_HANDLE_GAP_PX = 25;

export const clampGridGap = (value) => Math.max(5, Math.min(200, Number(value) || 25));

export const getBusLayout = (node, gridGapPx) => {
  const gridGap = BUS_HANDLE_GAP_PX;
  const angle = typeof node?.data?.angle === 'number' ? node.data.angle : VERTICAL_ORIENTATION;
  const horizontal = angle === VERTICAL_ORIENTATION;
  const width = Number(node?.width) || 0;
  const height = Number(node?.height) || 0;
  const storedLength = Number(node?.data?.busLength || node?.data?.attributes?.length?.value) || 0;
  const majorLength = horizontal ? (width || storedLength) : (height || storedLength);
  const snappedLength = Math.max(gridGap, Math.round((majorLength || gridGap) / gridGap) * gridGap);
  const segmentCount = Math.max(MIN_BUS_HANDLES - 1, Math.round(snappedLength / gridGap));
  const length = Math.max(gridGap, segmentCount * gridGap);
  const handleCount = Math.max(MIN_BUS_HANDLES, segmentCount + 1);
  const positions = Array.from({ length: handleCount }, (_, index) => index * gridGap);
  return {
    gridGap,
    angle,
    horizontal,
    handleCount,
    positions,
    length,
    thickness: BUS_THICKNESS_PX,
  };
};

export const parseBusHandleIndex = (handleId, handleCount) => {
  const match = /(\d+)$/.exec(String(handleId || ''));
  const parsed = match ? parseInt(match[1], 10) : null;
  if (Number.isFinite(parsed)) {
    return Math.min(Math.max(0, parsed), Math.max(0, handleCount - 1));
  }
  return Math.max(0, Math.floor(Math.max(0, handleCount - 1) / 2));
};

export const getBusHandleAnchor = (node, layout, role, handleId) => {
  const x = (node.positionAbsolute?.x ?? node.position?.x ?? 0);
  const y = (node.positionAbsolute?.y ?? node.position?.y ?? 0);
  const width = Number(node.width) || layout.thickness;
  const height = Number(node.height) || layout.thickness;
  const index = parseBusHandleIndex(handleId, layout.handleCount);
  const axisPos = layout.positions[index] ?? index * layout.gridGap;

  if (layout.horizontal) {
    if (role === 'target') {
      return { x: x + axisPos, y };
    }
    return { x: x + axisPos, y: y + height };
  }

  if (role === 'target') {
    return { x, y: y + axisPos };
  }
  return { x: x + width, y: y + axisPos };
};

export const enumerateBusHandles = (node, gridGapPx) => {
  const layout = getBusLayout(node, gridGapPx);
  const x = (node.positionAbsolute?.x ?? node.position?.x ?? 0);
  const y = (node.positionAbsolute?.y ?? node.position?.y ?? 0);
  const width = Number(node.width) || layout.thickness;
  const height = Number(node.height) || layout.thickness;
  const entries = [];

  layout.positions.forEach((axisPos, index) => {
    if (layout.horizontal) {
      entries.push({ role: 'target', id: `target-${index}`, x: x + axisPos, y });
      entries.push({ role: 'source', id: `handle-${index}`, x: x + axisPos, y: y + height });
    } else {
      entries.push({ role: 'target', id: `target-${index}`, x, y: y + axisPos });
      entries.push({ role: 'source', id: `handle-${index}`, x: x + width, y: y + axisPos });
    }
  });

  return { layout, handles: entries };
};
